import {ColumnDef, FilterDef, FilterOperator, FilterType, ListRequestIdModelTable} from "../../components/ExTable";
import {Asset, AssetField, AssetStatus, AssetType} from "../../app/api/model/asset";
import Page from "../../components/Layout/Page";
import {useAssetListQuery} from "../../app/api/api";
import {useTranslation} from "react-i18next";
import getAssetDetailPageLink from "../../app/api/model/asset/helper";
import getDetailPageLink from "../../utils/getDetailPageLink";
import {RoutePath} from "../../types";
import {capitalize, lowerCase} from "lodash";
import {getTranslationKey, TranslationContext} from "../../utils/getTranslationKey";
import {DateFormat, formatTimestamp} from "../../utils/datetime";
import {PlSentStatus} from "../../app/api/model/device";
import PlSentStatusChip from "../../components/PLSENTStatus";
import React from "react";

export default function AssetsList() {
    const {t} = useTranslation();

    const columnDefs: ColumnDef<Asset>[] = [
        {
            fieldName: AssetField.GLOBAL_ID,
            align: "left",
            label: t("generic_globalId"),
            sortable: true,
            width: 175,
            generateLink: (row: Asset) =>
                row[AssetField.GLOBAL_ID]
                    ? getAssetDetailPageLink(
                          row[AssetField.GLOBAL_ID],
                          row[AssetField.TYPE],
                          row[AssetField.ORGANIZATION_ID]
                      )
                    : "",
            sticky: true,
        },
        {
            fieldName: AssetField.LEOID,
            align: "left",
            label: t("generic_leoId"),
            sortable: true,
            width: 200,
        },
        {
            fieldName: AssetField.CUSTOMER_NUMBER,
            align: "left",
            label: t("generic_customerNumber"),
            sortable: true,
            width: 175,
        },
        {
            fieldName: AssetField.ORGANIZATION_NAME,
            align: "left",
            label: t("generic_companyName"),
            sortable: true,
            width: 175,
            generateLink: (row: Asset) =>
                row[AssetField.ORGANIZATION_ID]
                    ? getDetailPageLink(row[AssetField.ORGANIZATION_ID], RoutePath.ORGANIZATIONS)
                    : "",
        },
        {
            fieldName: AssetField.EXTERNAL_REFERENCE,
            align: "left",
            label: t("generic_externalReference"),
            sortable: true,
            headerInfo: t("assets_externalReferenceInfo"),
            width: 250,
        },
        {
            fieldName: AssetField.PL_SENT_ID,
            align: "left",
            label: t("label_plSentId"),
            width: 150,
            sortable: true,
        },
        {
            fieldName: AssetField.PL_SENT_STATUS,
            label: t("label_plSentStatus"),
            align: "center",
            sortable: true,
            width: 150,
            formatCellText: ({row}) => ({
                title: t(`plSent_status${capitalize(lowerCase(row[AssetField.PL_SENT_STATUS]))}`),
                node: <PlSentStatusChip status={row[AssetField.PL_SENT_STATUS]} />,
            }),
        },
        {
            fieldName: AssetField.ICCID,
            align: "left",
            label: t("generic_iccid"),
            sortable: true,
            width: 175,
        },
        {
            fieldName: AssetField.TYPE,
            align: "left",
            label: t("generic_type"),
            sortable: true,
            width: 150,
            formatCellText: ({cellValue}) =>
                t(getTranslationKey({context: TranslationContext.ASSET_TYPE, value: lowerCase(cellValue)})),
        },
        {
            fieldName: AssetField.CONNECTION_TYPE,
            align: "left",
            label: t("generic_connectionType"),
            sortable: true,
            width: 150,
        },
        {
            fieldName: AssetField.STATUS,
            align: "left",
            label: t("generic_status"),
            sortable: true,
            width: 150,
            formatCellText: ({cellValue}) => t(`generic_status${capitalize(cellValue)}`),
        },
        {
            fieldName: AssetField.CREATED_AT,
            align: "left",
            label: t("generic_createdAt"),
            sortable: true,
            width: 175,
            formatCellText: ({cellValue}) => {
                return formatTimestamp(cellValue, DateFormat.DATE_HOURS_MINUTES_SECONDS);
            },
        },
        {
            fieldName: AssetField.UPDATED_AT,
            align: "left",
            label: t("generic_updatedAt"),
            sortable: true,
            width: 175,
            formatCellText: ({cellValue}) => {
                return formatTimestamp(cellValue, DateFormat.DATE_HOURS_MINUTES_SECONDS);
            },
        },
        {
            fieldName: AssetField.PREDECESSOR_LEO_ID,
            align: "left",
            label: t("generic_predecessorLeoId"),
            width: 200,
            sortable: true,
        },
    ];

    const filterDefs: FilterDef<Asset>[] = [
        {
            fieldName: AssetField.LEOID,
            filterOperators: [FilterOperator.CONTAINS, FilterOperator.STARTSWITH, FilterOperator.EQ, FilterOperator.IN],
        },
        {
            fieldName: AssetField.GLOBAL_ID,
            filterOperators: [
                FilterOperator.EQ,
                FilterOperator.LT,
                FilterOperator.GT,
                FilterOperator.LTE,
                FilterOperator.GTE,
            ],
        },
        {
            fieldName: AssetField.PL_SENT_ID,
            label: t("label_plSentId"),
            filterOperators: [FilterOperator.CONTAINS, FilterOperator.STARTSWITH, FilterOperator.EQ, FilterOperator.IN],
        },
        {
            fieldName: AssetField.PL_SENT_STATUS,
            type: FilterType.ENUM,
            label: t("label_plSentStatus"),
            filterOperators: [FilterOperator.EQ, FilterOperator.NOT],
            enumValues: Object.keys(PlSentStatus).map((status) => {
                return {
                    value: status,
                    labelKey: `plSent_status${capitalize(lowerCase(status))}`,
                };
            }),
        },
        {
            fieldName: AssetField.CUSTOMER_NUMBER,
            filterOperators: [FilterOperator.CONTAINS, FilterOperator.STARTSWITH, FilterOperator.EQ, FilterOperator.IN],
        },
        {
            fieldName: AssetField.ORGANIZATION_NAME,
            filterOperators: [FilterOperator.CONTAINS, FilterOperator.STARTSWITH, FilterOperator.EQ, FilterOperator.IN],
        },
        {
            fieldName: AssetField.EXTERNAL_REFERENCE,
            filterOperators: [FilterOperator.CONTAINS, FilterOperator.STARTSWITH, FilterOperator.EQ, FilterOperator.IN],
        },
        {
            fieldName: AssetField.ICCID,
            filterOperators: [FilterOperator.CONTAINS, FilterOperator.STARTSWITH, FilterOperator.EQ, FilterOperator.IN],
        },
        {
            fieldName: AssetField.TYPE,
            type: FilterType.ENUM,
            filterOperators: [FilterOperator.EQ, FilterOperator.NOT],
            enumValues: Object.keys(AssetType).map((type) => {
                return {
                    value: type,
                    labelKey: type,
                };
            }),
        },
        {
            fieldName: AssetField.CONNECTION_TYPE,
            filterOperators: [FilterOperator.CONTAINS, FilterOperator.STARTSWITH, FilterOperator.EQ, FilterOperator.IN],
        },
        {
            fieldName: AssetField.STATUS,
            filterOperators: [FilterOperator.EQ, FilterOperator.NOT],
            type: FilterType.ENUM,
            enumValues: Object.keys(AssetStatus).map((status) => {
                return {
                    value: status,
                    labelKey: status,
                };
            }),
        },
        {
            fieldName: AssetField.CREATED_AT,
            filterOperators: [
                FilterOperator.EQ,
                FilterOperator.LT,
                FilterOperator.GT,
                FilterOperator.LTE,
                FilterOperator.GTE,
            ],
            type: FilterType.DATE,
        },
        {
            fieldName: AssetField.UPDATED_AT,
            filterOperators: [
                FilterOperator.EQ,
                FilterOperator.LT,
                FilterOperator.GT,
                FilterOperator.LTE,
                FilterOperator.GTE,
            ],
            type: FilterType.DATE,
        },
        {
            fieldName: AssetField.PREDECESSOR_LEO_ID,
            filterOperators: [FilterOperator.CONTAINS, FilterOperator.STARTSWITH, FilterOperator.EQ, FilterOperator.IN],
        },
    ];

    return (
        <Page>
            <ListRequestIdModelTable
                dataTest="assetsList"
                queryHook={useAssetListQuery}
                columnDefs={columnDefs}
                filterDefs={filterDefs}
                title={t("generic_assets")}
                selectableRows={false}
                compact={true}
            />
        </Page>
    );
}
